import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Info as InfoIcon,
  Article as TemplateIcon,
  Functions as FunctionsIcon,
  Timeline as TimelineIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { apiService } from '../services/api';
import { EstimationConfig as EstimationConfigType, TShirtSize, ComplexityMatrix, EffortMultipliers, EstimationMethodology } from '../types/estimation';

const EstimationConfig: React.FC = () => {
  const [configs, setConfigs] = useState<EstimationConfigType[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingConfig, setEditingConfig] = useState<Partial<EstimationConfigType> | null>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [isNewConfig, setIsNewConfig] = useState(false);
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);
  const [saveAsTemplateDialogOpen, setSaveAsTemplateDialogOpen] = useState(false);
  const [templateTypeInput, setTemplateTypeInput] = useState<string>('custom');
  const [templateDescriptionInput, setTemplateDescriptionInput] = useState<string>('');

  useEffect(() => {
    loadConfigs();
    loadTemplates();
  }, []);

  const loadConfigs = async () => {
    try {
      setLoading(true);
      const data = await apiService.getEstimationConfigs();
      setConfigs(data);
    } catch (error) {
      console.error('Error loading estimation configs:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async () => {
    try {
      const data = await apiService.getEstimationTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
    }
  };

  const handleCreateConfig = () => {
    setIsNewConfig(true);
    setEditingConfig({
      name: '',
      description: '',
      methodology: EstimationMethodology.T_SHIRT_SIZING,
      complexityMatrix: getDefaultComplexityMatrix(),
      effortMultipliers: getDefaultEffortMultipliers(),
      tShirtSizing: getDefaultTShirtSizing(),
      teamVelocity: 1.0,
      isActive: true,
      isDefault: false
    });
    setConfigDialogOpen(true);
  };

  const handleEditConfig = (config: EstimationConfigType) => {
    setIsNewConfig(false);
    setEditingConfig({ ...config });
    setConfigDialogOpen(true);
  };

  const handleSaveConfig = async () => {
    if (!editingConfig?.name) {
      alert('Configuration name is required');
      return;
    }

    try {
      if (isNewConfig) {
        await apiService.createEstimationConfig(editingConfig);
      } else {
        await apiService.updateEstimationConfig(editingConfig.id!, editingConfig);
      }
      
      setConfigDialogOpen(false);
      setEditingConfig(null);
      await loadConfigs();
    } catch (error) {
      console.error('Error saving config:', error);
    }
  };

  const handleDeleteConfig = async (configId: string) => {
    if (window.confirm('Are you sure you want to delete this configuration?')) {
      try {
        await apiService.deleteEstimationConfig(configId);
        await loadConfigs();
      } catch (error) {
        console.error('Error deleting config:', error);
      }
    }
  };

  const handleApplyTemplate = async () => {
    if (!selectedTemplate) return;
    
    try {
      const response = await apiService.applyTemplate(selectedTemplate.id, editingConfig);
      if (response?.success) {
        const appliedConfig = response.data?.appliedConfig || response.data;
        if (appliedConfig) {
          setEditingConfig(appliedConfig);
        }
        setTemplateDialogOpen(false);
        setSelectedTemplate(null);
      } else {
        alert(response?.message || 'Failed to apply template');
      }
    } catch (error) {
      console.error('Error applying template:', error);
      alert('Failed to apply template');
    }
  };

  const handleSaveAsTemplate = () => {
    if (!editingConfig?.name || !editingConfig?.methodology) {
      alert('Please provide a name and select a methodology before saving as template');
      return;
    }

    // Set default values
    setTemplateTypeInput('custom');
    setTemplateDescriptionInput(editingConfig.description || `Template based on ${editingConfig.name}`);
    setSaveAsTemplateDialogOpen(true);
  };

  const handleConfirmSaveAsTemplate = async () => {
    if (!templateDescriptionInput.trim()) {
      alert('Please provide a template description');
      return;
    }

    try {
      // Convert config to template format
      const templateData = {
        name: `${editingConfig!.name} (Template)`,
        description: templateDescriptionInput.trim(),
        templateType: templateTypeInput,
        methodology: editingConfig!.methodology,
        teamVelocity: editingConfig!.teamVelocity || 1.0,
        complexityMatrix: editingConfig!.complexityMatrix,
        effortMultipliers: editingConfig!.effortMultipliers,
        tShirtSizing: editingConfig!.tShirtSizing,
        functionPointAnalysis: editingConfig!.functionPointAnalysis || null,
        cocomoModel: editingConfig!.cocomoModel || null,
        isPublic: false, // User can make it public later
        isDefault: false
      };

      await apiService.createEstimationTemplate(templateData);
      alert('Template saved successfully! You can find it in the Estimation Templates page.');
      setSaveAsTemplateDialogOpen(false);
      await loadTemplates(); // Refresh templates list
    } catch (error: any) {
      console.error('Error saving template:', error);
      alert(`Failed to save template: ${error.response?.data?.message || error.message || 'Unknown error'}`);
    }
  };

  const getDefaultComplexityMatrix = (): ComplexityMatrix => ({
    frontend: {
      pageComplexity: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      uiComponents: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      stateManagement: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      apiIntegrations: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 }
    },
    backend: {
      dataModels: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      businessLogic: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      integrations: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      security: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 }
    },
    middleware: {
      messageQueues: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      caching: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      dataTransformation: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 }
    },
    database: {
      schemaComplexity: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      relationships: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 },
      performance: { xs: 1, s: 2, m: 3, l: 4, xl: 5, xxl: 5 }
    }
  });

  const getDefaultEffortMultipliers = (): EffortMultipliers => ({
    projectSetup: 1.1,
    management: 1.2,
    testing: 1.3,
    devOps: 1.1,
    documentation: 1.1,
    design: 1.2
  });

  const getDefaultTShirtSizing = (): Record<TShirtSize, number> => ({
    xs: 1,
    s: 3,
    m: 8,
    l: 20,
    xl: 50,
    xxl: 100
  });

  const updateComplexityFactor = (area: string, subArea: string, size: TShirtSize, value: number) => {
    if (!editingConfig?.complexityMatrix) return;

    setEditingConfig(prev => {
      if (!prev) return prev;
      
      const currentMatrix = prev.complexityMatrix || getDefaultComplexityMatrix();
      const currentArea = currentMatrix[area as keyof ComplexityMatrix] || {};
      const currentSubArea = (currentArea as any)[subArea] || {};
      
      return {
        ...prev,
        complexityMatrix: {
          ...currentMatrix,
          [area]: {
            ...currentArea,
            [subArea]: {
              ...currentSubArea,
              [size]: Math.max(1, Math.min(5, value))
            }
          }
        }
      };
    });
  };

  const updateEffortMultiplier = (field: string, value: number) => {
    setEditingConfig(prev => {
      if (!prev) return prev;
      
      const currentMultipliers = prev.effortMultipliers || getDefaultEffortMultipliers();
      
      return {
        ...prev,
        effortMultipliers: {
          ...currentMultipliers,
          [field]: Math.max(0.1, Math.min(5, value)) // Ensure value is between 0.1-5
        }
      };
    });
  };



  const updateTShirtSizing = (size: TShirtSize, value: number) => {
    setEditingConfig(prev => {
      if (!prev) return prev;
      
      const currentSizing = prev.tShirtSizing || {
        xs: 1,
        s: 3,
        m: 8,
        l: 20,
        xl: 50,
        xxl: 100
      };
      
      return {
        ...prev,
        tShirtSizing: {
          ...currentSizing,
          [size]: Math.max(0.5, Math.min(200, value))
        }
      };
    });
  };

  const updateFunctionPointAnalysis = (category: 'technicalComplexityFactors' | 'environmentalComplexityFactors', factor: string, value: number) => {
    setEditingConfig(prev => {
      if (!prev) return prev;
      
      const currentFP = prev.functionPointAnalysis || {
        externalInputs: { simple: 0, average: 0, complex: 0 },
        externalOutputs: { simple: 0, average: 0, complex: 0 },
        externalInquiries: { simple: 0, average: 0, complex: 0 },
        internalLogicalFiles: { simple: 0, average: 0, complex: 0 },
        externalInterfaceFiles: { simple: 0, average: 0, complex: 0 },
        technicalComplexityFactors: {
          dataCommunications: 3,
          distributedDataProcessing: 2,
          performance: 3,
          heavilyUsedConfiguration: 2,
          transactionRate: 3,
          onlineDataEntry: 3,
          endUserEfficiency: 3,
          onlineUpdate: 3,
          codeReusability: 2,
          installationEase: 3,
          operationalEase: 3,
          multipleSites: 1,
          facilitateChange: 2
        },
        environmentalComplexityFactors: {
          familiarWithDevelopmentProcess: 3,
          applicationExperience: 3,
          objectOrientedProgrammingExperience: 3,
          leadAnalystCapability: 3,
          motivation: 3,
          requirementsForStableRequirements: 3,
          partTimeStaff: 1,
          difficultProgrammingLanguage: 3
        }
      };
      
      return {
        ...prev,
        functionPointAnalysis: {
          ...currentFP,
          [category]: {
            ...currentFP[category],
            [factor]: Math.max(0, Math.min(5, value))
          }
        }
      };
    });
  };

  const updateCOCOMOModel = (field: string, value: any) => {
    setEditingConfig(prev => {
      if (!prev) return prev;
      
      const currentCOCOMO = prev.cocomoModel || {
        projectType: 'organic',
        linesOfCode: 10000,
        costDrivers: {
          requiredReliability: 1.0,
          databaseSize: 1.0,
          productComplexity: 1.0,
          executionTimeConstraint: 1.0,
          mainStorageConstraint: 1.0,
          computerTurnaroundTime: 1.0,
          analystCapability: 1.0,
          applicationsExperience: 1.0,
          programmerCapability: 1.0,
          virtualMachineExperience: 1.0,
          programmingLanguageExperience: 1.0,
          modernProgrammingPractices: 1.0,
          useOfSoftwareTools: 1.0,
          requiredDevelopmentSchedule: 1.0
        }
      };
      
      if (field === 'projectType' || field === 'linesOfCode') {
        return {
          ...prev,
          cocomoModel: {
            ...currentCOCOMO,
            [field]: value
          }
        };
      } else {
        // Update cost driver
        return {
          ...prev,
          cocomoModel: {
            ...currentCOCOMO,
            costDrivers: {
              ...currentCOCOMO.costDrivers,
              [field]: typeof value === 'number' ? Math.max(0.5, Math.min(1.5, value)) : value
            }
          }
        };
      }
    });
  };

  const getTShirtSizeColor = (size: TShirtSize) => {
    switch (size) {
      case TShirtSize.XS: return 'default';
      case TShirtSize.S: return 'primary';
      case TShirtSize.M: return 'secondary';
      case TShirtSize.L: return 'warning';
      case TShirtSize.XL: return 'error';
      case TShirtSize.XXL: return 'error';
      default: return 'default';
    }
  };

  const getMethodologyDescription = (methodology: EstimationMethodology): string => {
    switch (methodology) {
      case EstimationMethodology.T_SHIRT_SIZING:
        return 'T-shirt sizing is a relative estimation technique using XS, S, M, L, XL, XXL to categorize story complexity.';
      case EstimationMethodology.FUNCTION_POINTS:
        return 'Function Point Analysis is an industry standard method for measuring software size and complexity.';
      case EstimationMethodology.COCOMO:
        return 'COCOMO (Constructive Cost Model) is a regression model for estimating software development effort.';
      default:
        return '';
    }
  };

  const getMethodologyCalculationExplanation = (methodology: EstimationMethodology): string => {
    switch (methodology) {
      case EstimationMethodology.T_SHIRT_SIZING:
        return 'Story Size × Effort Multipliers = Final Effort. Example: Medium story (3 points) × 1.2 testing multiplier = 3.6 days';
      case EstimationMethodology.FUNCTION_POINTS:
        return 'Function Points × Complexity Factors × Effort Multipliers = Story Points. Example: 10 FP × 0.8 complexity × 1.2 multiplier = 9.6 story points';
      case EstimationMethodology.COCOMO:
        return 'Effort = a × (Size)^b × Effort Multipliers. Example: 1000 LOC × 2.4 (organic) × 1.3 multiplier = 3,120 person-hours';
      default:
        return '';
    }
  };

  const getMethodologyIcon = (methodology: EstimationMethodology) => {
    switch (methodology) {
      case EstimationMethodology.T_SHIRT_SIZING:
        return <AssessmentIcon />;
      case EstimationMethodology.FUNCTION_POINTS:
        return <FunctionsIcon />;
      case EstimationMethodology.COCOMO:
        return <TimelineIcon />;
      default:
        return <AssessmentIcon />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Estimation Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Configure T-shirt sizing, complexity matrix, and effort multipliers for story estimation
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<HelpIcon />}
            onClick={() => setHelpDialogOpen(true)}
          >
            How It Works
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateConfig}
          >
            Create Configuration
          </Button>
        </Box>
      </Box>

      {/* Existing Configurations */}
      <Grid container spacing={3}>
        {configs.map((config) => (
          <Grid item xs={12} md={6} lg={4} key={config.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h2">
                    {config.name}
                  </Typography>
                  <Box>
                    {config.isDefault && (
                      <Chip label="Default" color="primary" size="small" sx={{ mr: 1 }} />
                    )}
                    {config.isActive && (
                      <Chip label="Active" color="success" size="small" />
                    )}
                  </Box>
                </Box>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {config.description}
                </Typography>

                <Typography variant="body2">
                  <strong>Team Velocity:</strong> {config.teamVelocity}
                </Typography>
                
                <Typography variant="body2">
                  <strong>Created:</strong> {new Date(config.createdAt).toLocaleDateString()}
                </Typography>
              </CardContent>
              
              <CardActions>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditConfig(config)}
                >
                  Edit
                </Button>
                {!config.isDefault && (
                  <Button
                    size="small"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDeleteConfig(config.id)}
                  >
                    Delete
                  </Button>
                )}
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Configuration Dialog */}
      <Dialog open={configDialogOpen} onClose={() => setConfigDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {isNewConfig ? 'Create Estimation Configuration' : 'Edit Estimation Configuration'}
        </DialogTitle>
        <DialogContent>
          {editingConfig && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                {/* Basic Information */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Configuration Name"
                        value={editingConfig.name || ''}
                        onChange={(e) => setEditingConfig(prev => prev ? { ...prev, name: e.target.value } : prev)}
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Team Velocity"
                        type="number"
                        value={editingConfig.teamVelocity || 1.0}
                        onChange={(e) => setEditingConfig(prev => prev ? { ...prev, teamVelocity: parseFloat(e.target.value) || 1.0 } : prev)}
                        inputProps={{ min: 0.1, max: 10, step: 0.1 }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Description"
                        multiline
                        rows={2}
                        value={editingConfig.description || ''}
                        onChange={(e) => setEditingConfig(prev => prev ? { ...prev, description: e.target.value } : prev)}
                      />
                    </Grid>
                  </Grid>
                </Grid>

                {/* Estimation Methodology */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Estimation Methodology
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Estimation Methodology</InputLabel>
                        <Select
                          value={editingConfig.methodology || EstimationMethodology.T_SHIRT_SIZING}
                          onChange={(e) => {
                            const newMethodology = e.target.value as EstimationMethodology;
                            setEditingConfig(prev => {
                              if (!prev) return prev;
                              
                              // Update methodology and adapt configuration based on selected methodology
                              const updatedConfig = { ...prev, methodology: newMethodology };
                              
                              switch (newMethodology) {
                                case EstimationMethodology.T_SHIRT_SIZING:
                                  // Use T-shirt sizing defaults
                                  updatedConfig.complexityMatrix = {
                                    frontend: {
                                      pageComplexity: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      uiComponents: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      stateManagement: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      apiIntegrations: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 }
                                    },
                                    backend: {
                                      dataModels: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      businessLogic: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      integrations: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      security: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 }
                                    },
                                    middleware: {
                                      messageQueues: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      caching: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      dataTransformation: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 }
                                    },
                                    database: {
                                      schemaComplexity: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      relationships: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 },
                                      performance: { xs: 1, s: 2, m: 3, l: 5, xl: 8, xxl: 13 }
                                    }
                                  };
                                  updatedConfig.effortMultipliers = {
                                    projectSetup: 1.0,
                                    management: 1.1,
                                    testing: 1.2,
                                    devOps: 1.0,
                                    documentation: 1.1,
                                    design: 1.0
                                  };
                                  updatedConfig.tShirtSizing = { xs: 1, s: 3, m: 8, l: 20, xl: 50, xxl: 100 };
                                  break;
                                  
                                case EstimationMethodology.FUNCTION_POINTS:
                                  // Use Function Point Analysis defaults
                                  updatedConfig.functionPointAnalysis = {
                                    externalInputs: { simple: 0, average: 0, complex: 0 },
                                    externalOutputs: { simple: 0, average: 0, complex: 0 },
                                    externalInquiries: { simple: 0, average: 0, complex: 0 },
                                    internalLogicalFiles: { simple: 0, average: 0, complex: 0 },
                                    externalInterfaceFiles: { simple: 0, average: 0, complex: 0 },
                                    technicalComplexityFactors: {
                                      dataCommunications: 3,
                                      distributedDataProcessing: 2,
                                      performance: 3,
                                      heavilyUsedConfiguration: 2,
                                      transactionRate: 3,
                                      onlineDataEntry: 3,
                                      endUserEfficiency: 3,
                                      onlineUpdate: 3,
                                      codeReusability: 2,
                                      installationEase: 3,
                                      operationalEase: 3,
                                      multipleSites: 1,
                                      facilitateChange: 2
                                    },
                                    environmentalComplexityFactors: {
                                      familiarWithDevelopmentProcess: 3,
                                      applicationExperience: 3,
                                      objectOrientedProgrammingExperience: 3,
                                      leadAnalystCapability: 3,
                                      motivation: 3,
                                      requirementsForStableRequirements: 3,
                                      partTimeStaff: 1,
                                      difficultProgrammingLanguage: 3
                                    }
                                  };
                                  // Keep minimal defaults for required fields but not used for FP
                                  updatedConfig.complexityMatrix = getDefaultComplexityMatrix();
                                  updatedConfig.effortMultipliers = getDefaultEffortMultipliers();
                                  updatedConfig.tShirtSizing = getDefaultTShirtSizing();
                                  break;
                                  
                                case EstimationMethodology.COCOMO:
                                  // Use COCOMO defaults
                                  updatedConfig.cocomoModel = {
                                    projectType: 'organic',
                                    linesOfCode: 10000,
                                    costDrivers: {
                                      requiredReliability: 1.0,
                                      databaseSize: 1.0,
                                      productComplexity: 1.0,
                                      executionTimeConstraint: 1.0,
                                      mainStorageConstraint: 1.0,
                                      computerTurnaroundTime: 1.0,
                                      analystCapability: 1.0,
                                      applicationsExperience: 1.0,
                                      programmerCapability: 1.0,
                                      virtualMachineExperience: 1.0,
                                      programmingLanguageExperience: 1.0,
                                      modernProgrammingPractices: 1.0,
                                      useOfSoftwareTools: 1.0,
                                      requiredDevelopmentSchedule: 1.0
                                    }
                                  };
                                  // Keep minimal defaults for required fields but not used for COCOMO
                                  updatedConfig.complexityMatrix = getDefaultComplexityMatrix();
                                  updatedConfig.effortMultipliers = getDefaultEffortMultipliers();
                                  updatedConfig.tShirtSizing = getDefaultTShirtSizing();
                                  break;
                              }
                              
                              return updatedConfig;
                            });
                          }}
                          label="Estimation Methodology"
                        >
                          <MenuItem value={EstimationMethodology.T_SHIRT_SIZING}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AssessmentIcon />
                              T-Shirt Sizing
                            </Box>
                          </MenuItem>
                          <MenuItem value={EstimationMethodology.FUNCTION_POINTS}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <FunctionsIcon />
                              Function Point Analysis
                            </Box>
                          </MenuItem>
                          <MenuItem value={EstimationMethodology.COCOMO}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <TimelineIcon />
                              COCOMO Model
                            </Box>
                          </MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Button
                        variant="outlined"
                        startIcon={<TemplateIcon />}
                        onClick={() => setTemplateDialogOpen(true)}
                        fullWidth
                        sx={{ height: 56 }}
                      >
                        Apply Template
                      </Button>
                    </Grid>
                    <Grid item xs={12}>
                      <Alert severity="info" sx={{ mt: 1 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {getMethodologyDescription(editingConfig.methodology || EstimationMethodology.T_SHIRT_SIZING)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                          💡 <strong>How this affects estimation:</strong>
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>
                          {getMethodologyCalculationExplanation(editingConfig.methodology || EstimationMethodology.T_SHIRT_SIZING)}
                        </Typography>
                      </Alert>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Methodology-Specific Configuration */}
                
                {/* T-Shirt Sizing Configuration - ONLY for T_SHIRT_SIZING */}
                {editingConfig.methodology === EstimationMethodology.T_SHIRT_SIZING && (
                  <Grid item xs={12}>
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">T-shirt Sizing (Story Points Mapping)</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Configure the story point values for each T-shirt size. These values will be used as the base for estimation calculations.
                        </Alert>
                        <Grid container spacing={2}>
                          {Object.values(TShirtSize).map((size) => (
                            <Grid item xs={6} md={4} key={size}>
                              <TextField
                                fullWidth
                                label={`${size.toUpperCase()} (Story Points)`}
                                type="number"
                                value={editingConfig.tShirtSizing?.[size] || 0}
                                onChange={(e) => updateTShirtSizing(size, parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0.5, max: 200, step: 0.5 }}
                                helperText={`Default: ${size === TShirtSize.XS ? '1' : size === TShirtSize.S ? '3' : size === TShirtSize.M ? '8' : size === TShirtSize.L ? '20' : size === TShirtSize.XL ? '50' : '100'}`}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

                {/* Function Point Analysis Configuration - ONLY for FUNCTION_POINTS */}
                {editingConfig.methodology === EstimationMethodology.FUNCTION_POINTS && (
                  <Grid item xs={12}>
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Function Point Analysis Configuration (IFPUG Standard)</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Alert severity="info" sx={{ mb: 3 }}>
                          Configure default Function Point counts and complexity factors. These are defaults applied to stories unless overridden per-story.
                          <br /><strong>Note:</strong> Actual FP counts are collected per-story during estimation. These settings provide defaults and technical/environmental factors.
                        </Alert>
                        
                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                          Technical Complexity Factors (TCF) - Scale 0-5
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          These factors adjust the function point count based on technical complexity. Each factor ranges from 0 (not present) to 5 (essential).
                        </Typography>
                        <Grid container spacing={2}>
                          {['dataCommunications', 'distributedDataProcessing', 'performance', 'heavilyUsedConfiguration', 'transactionRate', 'onlineDataEntry', 'endUserEfficiency', 'onlineUpdate', 'codeReusability', 'installationEase', 'operationalEase', 'multipleSites', 'facilitateChange'].map((factor) => (
                            <Grid item xs={12} md={6} lg={4} key={factor}>
                              <TextField
                                fullWidth
                                label={factor.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                type="number"
                                value={editingConfig.functionPointAnalysis?.technicalComplexityFactors?.[factor as keyof typeof editingConfig.functionPointAnalysis.technicalComplexityFactors] || 3}
                                onChange={(e) => updateFunctionPointAnalysis('technicalComplexityFactors', factor, parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 5, step: 1 }}
                              />
                            </Grid>
                          ))}
                        </Grid>

                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 3, fontWeight: 'bold' }}>
                          Environmental Complexity Factors (ECF) - Scale 0-5
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          These factors adjust function points based on team experience and development environment.
                        </Typography>
                        <Grid container spacing={2}>
                          {['familiarWithDevelopmentProcess', 'applicationExperience', 'objectOrientedProgrammingExperience', 'leadAnalystCapability', 'motivation', 'requirementsForStableRequirements', 'partTimeStaff', 'difficultProgrammingLanguage'].map((factor) => (
                            <Grid item xs={12} md={6} lg={4} key={factor}>
                              <TextField
                                fullWidth
                                label={factor.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                                type="number"
                                value={editingConfig.functionPointAnalysis?.environmentalComplexityFactors?.[factor as keyof typeof editingConfig.functionPointAnalysis.environmentalComplexityFactors] || 3}
                                onChange={(e) => updateFunctionPointAnalysis('environmentalComplexityFactors', factor, parseFloat(e.target.value) || 0)}
                                inputProps={{ min: 0, max: 5, step: 1 }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

                {/* COCOMO Configuration - ONLY for COCOMO */}
                {editingConfig.methodology === EstimationMethodology.COCOMO && (
                  <Grid item xs={12}>
                    <Accordion defaultExpanded>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">COCOMO Model Configuration</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Alert severity="info" sx={{ mb: 3 }}>
                          Configure COCOMO model parameters. These settings define default project characteristics.
                          <br /><strong>Note:</strong> Lines of Code are typically collected per-story. The default here is for fallback.
                        </Alert>
                        
                        <Grid container spacing={2} sx={{ mb: 3 }}>
                          <Grid item xs={12} md={6}>
                            <FormControl fullWidth>
                              <InputLabel>Project Type</InputLabel>
                              <Select
                                value={editingConfig.cocomoModel?.projectType || 'organic'}
                                onChange={(e) => updateCOCOMOModel('projectType', e.target.value)}
                                label="Project Type"
                              >
                                <MenuItem value="organic">
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Organic</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Small teams, familiar environment, simple requirements
                                    </Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value="semi-detached">
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Semi-Detached</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Medium teams, mixed experience, moderate requirements
                                    </Typography>
                                  </Box>
                                </MenuItem>
                                <MenuItem value="embedded">
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Embedded</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Large teams, tight constraints, complex requirements
                                    </Typography>
                                  </Box>
                                </MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Default Lines of Code (LOC)"
                              type="number"
                              value={editingConfig.cocomoModel?.linesOfCode || 10000}
                              onChange={(e) => updateCOCOMOModel('linesOfCode', parseFloat(e.target.value) || 10000)}
                              inputProps={{ min: 1000, max: 1000000, step: 1000 }}
                              helperText="Default LOC for stories without explicit LOC estimates"
                            />
                          </Grid>
                        </Grid>

                        <Typography variant="subtitle1" gutterBottom sx={{ mt: 2, fontWeight: 'bold' }}>
                          Cost Drivers - Scale 0.5-1.5 (affect effort multiplier)
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          These cost drivers adjust the base effort estimate. Lower values reduce effort, higher values increase it.
                        </Typography>
                        <Grid container spacing={2}>
                          {[
                            { key: 'requiredReliability', label: 'Required Reliability' },
                            { key: 'databaseSize', label: 'Database Size' },
                            { key: 'productComplexity', label: 'Product Complexity' },
                            { key: 'executionTimeConstraint', label: 'Execution Time Constraint' },
                            { key: 'mainStorageConstraint', label: 'Main Storage Constraint' },
                            { key: 'computerTurnaroundTime', label: 'Computer Turnaround Time' },
                            { key: 'analystCapability', label: 'Analyst Capability' },
                            { key: 'applicationsExperience', label: 'Applications Experience' },
                            { key: 'programmerCapability', label: 'Programmer Capability' },
                            { key: 'virtualMachineExperience', label: 'Virtual Machine Experience' },
                            { key: 'programmingLanguageExperience', label: 'Programming Language Experience' },
                            { key: 'modernProgrammingPractices', label: 'Modern Programming Practices' },
                            { key: 'useOfSoftwareTools', label: 'Use of Software Tools' },
                            { key: 'requiredDevelopmentSchedule', label: 'Required Development Schedule' }
                          ].map(({ key, label }) => (
                            <Grid item xs={12} md={6} lg={4} key={key}>
                              <TextField
                                fullWidth
                                label={label}
                                type="number"
                                value={editingConfig.cocomoModel?.costDrivers?.[key as keyof typeof editingConfig.cocomoModel.costDrivers] || 1.0}
                                onChange={(e) => updateCOCOMOModel(key, parseFloat(e.target.value) || 1.0)}
                                inputProps={{ min: 0.5, max: 1.5, step: 0.1 }}
                              />
                            </Grid>
                          ))}
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}


                {/* Effort Multipliers - ONLY for T_SHIRT_SIZING */}
                {editingConfig.methodology === EstimationMethodology.T_SHIRT_SIZING && (
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">
                          Effort Multipliers (Project Factors)
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          These multipliers adjust the base story points to account for project overhead and additional effort factors.
                        </Alert>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Project Setup"
                              type="number"
                              value={editingConfig.effortMultipliers?.projectSetup || 1.0}
                              onChange={(e) => updateEffortMultiplier('projectSetup', parseFloat(e.target.value) || 1.0)}
                              inputProps={{ min: 0.1, max: 5, step: 0.1 }}
                              helperText="Environment setup, tooling overhead"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Management"
                              type="number"
                              value={editingConfig.effortMultipliers?.management || 1.0}
                              onChange={(e) => updateEffortMultiplier('management', parseFloat(e.target.value) || 1.0)}
                              inputProps={{ min: 0.1, max: 5, step: 0.1 }}
                              helperText="Planning, coordination, reporting"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Testing"
                              type="number"
                              value={editingConfig.effortMultipliers?.testing || 1.0}
                              onChange={(e) => updateEffortMultiplier('testing', parseFloat(e.target.value) || 1.0)}
                              inputProps={{ min: 0.1, max: 5, step: 0.1 }}
                              helperText="Unit, integration, system testing"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="DevOps"
                              type="number"
                              value={editingConfig.effortMultipliers?.devOps || 1.0}
                              onChange={(e) => updateEffortMultiplier('devOps', parseFloat(e.target.value) || 1.0)}
                              inputProps={{ min: 0.1, max: 5, step: 0.1 }}
                              helperText="Deployment, CI/CD, monitoring"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Documentation"
                              type="number"
                              value={editingConfig.effortMultipliers?.documentation || 1.0}
                              onChange={(e) => updateEffortMultiplier('documentation', parseFloat(e.target.value) || 1.0)}
                              inputProps={{ min: 0.1, max: 5, step: 0.1 }}
                              helperText="Technical docs, user guides"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Design"
                              type="number"
                              value={editingConfig.effortMultipliers?.design || 1.2}
                              onChange={(e) => updateEffortMultiplier('design', parseFloat(e.target.value) || 1.0)}
                              inputProps={{ min: 0.1, max: 5, step: 0.1 }}
                              helperText="UI/UX design, architecture design"
                            />
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}

                {/* Complexity Matrix - ONLY for T_SHIRT_SIZING */}
                {editingConfig.methodology === EstimationMethodology.T_SHIRT_SIZING && (
                  <Grid item xs={12}>
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6">Complexity Matrix</Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Configure complexity factors (1-5 scale) for different technical areas across T-shirt sizes.
                          This matrix helps adjust base story points based on technical complexity.
                        </Alert>
                        
                        {editingConfig.complexityMatrix && (
                          <Box>
                            {Object.entries(editingConfig.complexityMatrix).map(([area, subAreas]) => (
                              <Accordion key={area} sx={{ mb: 1 }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                  <Typography variant="subtitle1" sx={{ textTransform: 'capitalize' }}>
                                    {area}
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <Grid container spacing={2}>
                                    {Object.entries(subAreas).map(([subArea, values]) => {
                                      const typedValues = values as Record<TShirtSize, number>;
                                      return (
                                        <Grid item xs={12} key={subArea}>
                                          <Typography variant="subtitle2" sx={{ mb: 1, textTransform: 'capitalize' }}>
                                            {subArea.replace(/([A-Z])/g, ' $1').trim()}
                                          </Typography>
                                          <Grid container spacing={1}>
                                            {Object.values(TShirtSize).map((size) => (
                                              <Grid item xs={6} md={4} key={size}>
                                                <TextField
                                                  size="small"
                                                  label={size.toUpperCase()}
                                                  type="number"
                                                  value={typedValues[size] || 1}
                                                  onChange={(e) => updateComplexityFactor(area, subArea, size, parseInt(e.target.value) || 1)}
                                                  inputProps={{ min: 1, max: 5 }}
                                                />
                                              </Grid>
                                            ))}
                                          </Grid>
                                        </Grid>
                                      );
                                    })}
                                  </Grid>
                                </AccordionDetails>
                              </Accordion>
                            ))}
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfigDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveAsTemplate} 
            variant="outlined"
            startIcon={<TemplateIcon />}
            disabled={!editingConfig?.name || !editingConfig?.methodology}
          >
            Save as Template
          </Button>
          <Button onClick={handleSaveConfig} variant="contained">
            Save Configuration
          </Button>
        </DialogActions>
      </Dialog>

      {/* Save as Template Dialog */}
      <Dialog open={saveAsTemplateDialogOpen} onClose={() => setSaveAsTemplateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TemplateIcon color="primary" />
            Save Configuration as Template
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Save this estimation configuration as a reusable template for future projects.
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Template Type *</InputLabel>
                  <Select
                    value={templateTypeInput}
                    onChange={(e) => setTemplateTypeInput(e.target.value)}
                    label="Template Type *"
                  >
                    <MenuItem value="web_application">Web Application</MenuItem>
                    <MenuItem value="mobile_app">Mobile App</MenuItem>
                    <MenuItem value="api_development">API Development</MenuItem>
                    <MenuItem value="ecommerce">E-commerce</MenuItem>
                    <MenuItem value="cms">CMS</MenuItem>
                    <MenuItem value="dashboard">Dashboard</MenuItem>
                    <MenuItem value="desktop_application">Desktop Application</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Template Description *"
                  multiline
                  rows={3}
                  value={templateDescriptionInput}
                  onChange={(e) => setTemplateDescriptionInput(e.target.value)}
                  placeholder="Describe what this template is best used for..."
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    <strong>Template Name:</strong> {editingConfig?.name || 'Untitled'} (Template)
                    <br />
                    <strong>Methodology:</strong> {editingConfig?.methodology?.replace('_', ' ') || 'Not selected'}
                  </Typography>
                </Alert>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveAsTemplateDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmSaveAsTemplate} 
            variant="contained"
            disabled={!templateDescriptionInput.trim()}
          >
            Save Template
          </Button>
        </DialogActions>
      </Dialog>

      {/* Help Dialog */}
      <Dialog open={helpDialogOpen} onClose={() => setHelpDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HelpIcon color="primary" />
            <Typography variant="h6">How Estimation Calculation Works</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom color="primary">
              📊 Industry Standard Estimation Methodology
            </Typography>
            
            <Typography variant="body2" paragraph>
              This estimation system combines <strong>T-shirt Sizing</strong>, <strong>Complexity Matrix</strong>, and <strong>Effort Multipliers</strong> 
              based on industry best practices from Agile methodologies, COCOMO, and Function Point Analysis.
            </Typography>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              🎯 Step-by-Step Calculation Process
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Step 1: T-shirt Sizing (Story Points)
              </Typography>
              <Typography variant="body2" paragraph>
                Stories are categorized by complexity using T-shirt sizes, which are then converted to story points:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 2 }}>
                <Chip label="XS: 1 story point" color="default" size="small" />
                <Chip label="S: 3 story points" color="primary" size="small" />
                <Chip label="M: 8 story points" color="secondary" size="small" />
                <Chip label="L: 20 story points" color="warning" size="small" />
                <Chip label="XL: 50 story points" color="error" size="small" />
                <Chip label="XXL: 100 story points" color="error" size="small" />
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Step 2: Complexity Matrix Analysis
              </Typography>
              <Typography variant="body2" paragraph>
                For each story, complexity factors are assessed on a 1-5 scale across technical areas:
              </Typography>
              
              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Frontend Complexity Factors</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 1 }}>
                    <Typography variant="body2">• <strong>Page Complexity:</strong> UI layout complexity</Typography>
                    <Typography variant="body2">• <strong>UI Components:</strong> Custom component count</Typography>
                    <Typography variant="body2">• <strong>State Management:</strong> Data flow complexity</Typography>
                    <Typography variant="body2">• <strong>API Integrations:</strong> External API calls</Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Backend Complexity Factors</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 1 }}>
                    <Typography variant="body2">• <strong>Data Models:</strong> Database schema complexity</Typography>
                    <Typography variant="body2">• <strong>Business Logic:</strong> Business rules complexity</Typography>
                    <Typography variant="body2">• <strong>Integrations:</strong> External system integrations</Typography>
                    <Typography variant="body2">• <strong>Security:</strong> Security requirements complexity</Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Accordion sx={{ mb: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle2">Middleware & Database Factors</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 1 }}>
                    <Typography variant="body2">• <strong>Message Queues:</strong> Messaging complexity</Typography>
                    <Typography variant="body2">• <strong>Caching:</strong> Caching strategy complexity</Typography>
                    <Typography variant="body2">• <strong>Schema Complexity:</strong> Database relationships</Typography>
                    <Typography variant="body2">• <strong>Performance:</strong> Query optimization needs</Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Step 3: Effort Calculation Formula
              </Typography>
              <Box sx={{ backgroundColor: 'grey.50', p: 2, borderRadius: 1, fontFamily: 'monospace' }}>
                <Typography variant="body2" component="div">
                  <strong>Base Story Points</strong> = T-shirt size value (e.g., M = 8)<br/>
                  <strong>Complexity Factor</strong> = Average of relevant complexity factors<br/>
                  <strong>Adjusted Story Points</strong> = Base Story Points × Complexity Factor<br/>
                  <strong>Person Days</strong> = Adjusted Story Points × Team Velocity<br/>
                  <strong>Total Effort</strong> = Person Days × Effort Multipliers
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                Step 4: Effort Multipliers
              </Typography>
              <Typography variant="body2" paragraph>
                Additional effort factors applied to account for project overhead:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 1 }}>
                <Typography variant="body2">• <strong>Project Setup:</strong> Environment setup, tooling</Typography>
                <Typography variant="body2">• <strong>Management:</strong> Planning, coordination, reporting</Typography>
                <Typography variant="body2">• <strong>Testing:</strong> Unit, integration, system testing</Typography>
                <Typography variant="body2">• <strong>DevOps:</strong> Deployment, CI/CD, monitoring</Typography>
                <Typography variant="body2">• <strong>Documentation:</strong> Technical docs, user guides</Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                📋 Real-World Example
              </Typography>
              <Box sx={{ backgroundColor: 'primary.50', p: 2, borderRadius: 1 }}>
                <Typography variant="body2" component="div">
                  <strong>Story:</strong> "User Registration with Email Verification"<br/>
                  <strong>T-shirt Size:</strong> M (8 story points)<br/>
                  <strong>Complexity Factors:</strong> Frontend: 2.5, Backend: 2.25, Database: 1.33<br/>
                  <strong>Average Complexity:</strong> (2.5 + 2.25 + 1.33) ÷ 3 = 2.03<br/>
                  <strong>Adjusted Story Points:</strong> 8 × 2.03 = 16.24<br/>
                  <strong>Person Days:</strong> 16.24 × 1.0 = 16.24<br/>
                  <strong>Total Effort:</strong> 16.24 × 1.73 = 28.1 person days
                </Typography>
              </Box>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                🌟 Industry Standards & Best Practices
              </Typography>
              <Typography variant="body2" paragraph>
                This methodology is based on established industry standards:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    📊 Agile Methodologies
                  </Typography>
                  <Typography variant="body2">
                    • T-shirt sizing from Scrum and Kanban<br/>
                    • Story point estimation<br/>
                    • Velocity tracking
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    🏗️ COCOMO Model
                  </Typography>
                  <Typography variant="body2">
                    • Cost estimation model<br/>
                    • Effort multipliers<br/>
                    • Complexity factors
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>
                    📈 Function Point Analysis
                  </Typography>
                  <Typography variant="body2">
                    • Complexity matrix approach<br/>
                    • Technical factor analysis<br/>
                    • Effort estimation
                  </Typography>
                </Box>
              </Box>
            </Box>

            <Box sx={{ backgroundColor: 'info.50', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'info.200' }}>
              <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold', color: 'info.main' }}>
                💡 Pro Tips
              </Typography>
              <Typography variant="body2">
                • <strong>Calibrate regularly:</strong> Adjust complexity factors based on team performance<br/>
                • <strong>Use historical data:</strong> Compare estimates with actual effort<br/>
                • <strong>Team velocity varies:</strong> Junior teams: 0.7, Senior teams: 1.3<br/>
                • <strong>Break down large stories:</strong> XXL stories should be decomposed<br/>
                • <strong>Review estimates:</strong> Conduct estimation review sessions
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)}>
            Got It!
          </Button>
        </DialogActions>
      </Dialog>

      {/* Template Selection Dialog */}
      <Dialog open={templateDialogOpen} onClose={() => setTemplateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TemplateIcon />
            Apply Estimation Template
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Select a template to apply predefined estimation settings to your configuration.
          </Typography>
          
          <Grid container spacing={2}>
            {templates.map((template) => (
              <Grid item xs={12} md={6} key={template.id}>
                <Card 
                  sx={{ 
                    cursor: 'pointer',
                    border: selectedTemplate?.id === template.id ? 2 : 1,
                    borderColor: selectedTemplate?.id === template.id ? 'primary.main' : 'divider'
                  }}
                  onClick={() => setSelectedTemplate(template)}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      {getMethodologyIcon(template.methodology)}
                      <Typography variant="h6" component="h3">
                        {template.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.description}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        label={template.templateType} 
                        size="small" 
                        variant="outlined" 
                      />
                      {template.isDefault && (
                        <Chip label="Default" size="small" color="primary" />
                      )}
                      {template.usageCount > 0 && (
                        <Chip label={`${template.usageCount} uses`} size="small" variant="outlined" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTemplateDialogOpen(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApplyTemplate}
            disabled={!selectedTemplate}
          >
            Apply Template
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EstimationConfig;
